import "../msa/MSAPageContainer.scss";

import { SECTION } from "constants/SettingsConstants";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { setService, setValidation } from "reducers/future-medical";
import { setCurrentSection, setValidationState } from "reducers/main";
import { useGetCostProjectionValidationByIdQuery } from "services/cost-projection-api";
import type {
  CostProjectionDataType,
  ValidationDataType,
} from "types/DataTypes";
import { useToken } from "utils/context";
import { useDispatch } from "utils/redux";

type CostProjectionPageContainerPropTypes = {
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  layout: "full" | "split";
  service: CostProjectionDataType;
};

const CostProjectionPageContainer = ({
  header,
  footer,
  children,
  layout = "full",
  service,
}: CostProjectionPageContainerPropTypes) => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { costProjectionId } = useParams<{ costProjectionId: string }>();
  const location = useLocation();

  // determine if it's mcp or lcp from the path
  const isMcp = location.pathname.includes("/mcp/");
  const serviceType = isMcp ? "mcp" : "lcp";

  const { data: costProjectionValidation } =
    useGetCostProjectionValidationByIdQuery(
      { costProjectionId },
      { skip: !costProjectionId || !accessToken },
    );

  useEffect(() => {
    const section = serviceType === "mcp" ? SECTION.MCP : SECTION.LCP;

    dispatch(setCurrentSection(section));
  }, [dispatch, serviceType]);

  useEffect(() => {
    if (service) {
      dispatch(setService(service));
    }
  }, [dispatch, service]);

  useEffect(() => {
    dispatch(setValidation(costProjectionValidation));
  }, [dispatch, costProjectionValidation]);

  useEffect(() => {
    if (costProjectionValidation?.errors) {
      const extractedErrors: ValidationDataType[] =
        costProjectionValidation.errors.filter(
          (error: ValidationDataType) => error.level === "ERROR",
        );

      const extractedWarnings: ValidationDataType[] =
        costProjectionValidation.errors.filter(
          (warning: ValidationDataType) => warning.level === "WARN",
        );

      dispatch(
        setValidationState({
          errors: extractedErrors,
          warnings: extractedWarnings,
        }),
      );
    }
  }, [dispatch, costProjectionValidation]);

  return (
    <div className="swim-wrapper h-100">
      {header && <div className="MSAPageContainer__Header">{header}</div>}
      <div
        className={`grid-layout gl-2 no-gap-tb swimlanes msa-medical-${layout}`}
      >
        {children}
        {footer && <div className="MSAPageContainer__Footer">{footer}</div>}
      </div>
    </div>
  );
};

export default CostProjectionPageContainer;
